import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { callAddressType, createForm as createFormAddress } from "../../../../components/address/address.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionOpenUrl,
    createRowActionReStatus,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import {
    email,
    isAlphabetOrNumber,
    noHyphenValidator,
    required,
    requiredWhen,
    validateError
} from "../../../../utils/validate";
import { createForm as createFormStorageLocation } from "../storage-location/storage-location.logic";
import { openModalForm as openModalFormConfirmApprove } from "./modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmReceive } from "./modal/confirm-receive.logic";
import { openModalForm as openModalFormConfirmRequest } from "./modal/confirm-request.logic";
import { createConditionAction, createConditionForm } from "./request.condition";
import { callRequestDoc, callRequestStatus, callRequestType, callStorageLocationAttachment, callManufactureLocationAttachment, createColumns, createField } from "./request.section";
import { callRequestDocGenType, callUser, callUserCompanyMe } from "./requestor/requestor.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callReportStorageLocation = ({ data }) => store.dispatch(`${actionPrefix}/reportStorageLocation`, { data });

export const onSearch = ({ props, page, state }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refStorageLocationTemp.refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refStorageLocationTemp.refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refStorageLocationTemp.refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const rulesTop = createRulesTop({ props, data, refs });
    const rules1 = createRulesTab1({ props, data, refs });
    const rules2 = createRulesTab2({ props, data, refs });
    const rules3 = createRulesTab3({ props, data, refs });
    const rules4 = createRulesTab4({ props, data, refs });
    let tabKey = null;
    return data.useForm
        .validate(Object.keys(rulesTop))
        .then(() => {
            tabKey = "Requestor";
            return data.useForm.validate(Object.keys(rules1));
        })
        .then(() => {
            tabKey = "StorageLocation";
            return data.useForm.validate(Object.keys(rules2));
        })
        .then(() => {
            tabKey = "StorageLocationSub";
            return data.useForm.validate(Object.keys(rules3));
        })
        .then(() => {
            tabKey = "Attachment";
            return data.useForm.validate(Object.keys(rules4));
        })
        .catch(error => {
            if (tabKey) {
                menuContext.tabKey = tabKey;
            }
            validateError({ error });
            throw error;
        });
};

export const onClickSave = ({ props, data, refs, state, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs, state });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: [
                "**",
                "refStorageLocation.id",
                "refStorageLocation.approveCode",
                "refStorageLocation.refUserCompany.*",
                "refStorageLocation.refUserCompany.refPersonType.*",
                "refStorageLocationTemp.**",
                "refStorageLocationTemp.refUserCompany.**",
                "refStorageLocationTemp.refUser.**",
                "refStorageLocationNew.id",
                "refStorageLocationNew.approveCode",
                "refStorageLocationNew.expireDate"
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "refStorageLocationTemp.refUserCompany.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refStorageLocationTemp.refUser.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refStorageLocationTemp.addresses",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                { name: "refStorageLocationTemp.types", select: ["*", "refLocationType.*"], page: 0, size: 1000000 },
                { name: "refStorageLocationTemp.productTypes", select: ["*", "refLocationProductType.*"], page: 0, size: 1000000 },
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitApprove",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitApprove" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext, isLocation } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refStorageLocationTemp.refUser.username", type: "String", operator: "=", value: userInfo.username }]);
        if (userInfo.isCeo) {
            whereGroup.push([{ name: "refStorageLocationTemp.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }]);
        }
    }
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([{ name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [
        { name: "refRequestDoc.key", type: "String", operator: "=", value: isLocation ? "06" : "07" },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        isSignCa: null,
        refStorageLocation: null,
        refStorageLocationTemp: { ...createFormStorageLocation(), addresses: [createFormAddress()] },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            waitReceive: [{ remark: null, statusDatetime: null }],
            waitApprove: [{ remark: null, statusDatetime: null }],
            approve: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forReceiveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    return {
        ...createRulesTop({ props, data, refs, key }),
        ...createRulesTab1({ props, data, refs, key }),
        ...createRulesTab2({ props, data, refs, key }),
        ...createRulesTab3({ props, data, refs, key }),
        ...createRulesTab4({ props, data, refs, key })
    };
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDocGenType`]: [required],
        [`${prefix}requestCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refStorageLocationTemp.approveCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refStorageLocation`]: [requiredWhen({ when: () => ["2", "3", "4"].includes(model.refRequestType?.key) })],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && ["2", "4"].includes(model.refRequestType?.key) && (!model.id || ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesTab1 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refStorageLocationTemp.refUserCompany`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refStorageLocationTemp.refUserCompany?.id })
        ],
        [`${prefix}refStorageLocationTemp.refUser`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refStorageLocationTemp.refUser?.id })
        ]
    };
};

export const createRulesTab2 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refStorageLocationTemp.name`]: [required],
        [`${prefix}refStorageLocationTemp.idTax`]: [required],
        [`${prefix}refStorageLocationTemp.email`]: [required, email],
        [`${prefix}refStorageLocationTemp.addresses.0.refAddressType`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.noTh`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.noEn`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.refProvince`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.refAmphur`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.refTambon`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.postCode`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.tel`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.latitude`]: [required],
        [`${prefix}refStorageLocationTemp.addresses.0.longitude`]: [required],
        [`${prefix}refStorageLocationTemp.refOwnershipType`]: [required],
        [`${prefix}refStorageLocationTemp.isSuitablePosition`]: [required],
        [`${prefix}refStorageLocationTemp.isRoyalPalace`]: [required],
        [`${prefix}refStorageLocationTemp.isHome`]: [required],
        [`${prefix}refStorageLocationTemp.isShoppingArea`]: [required],
        [`${prefix}refStorageLocationTemp.isPublicPlace`]: [required],
        [`${prefix}refStorageLocationTemp.isIndustrialArea`]: [required],
        [`${prefix}refStorageLocationTemp.isSafety`]: [requiredWhen({ when: () => !model.refStorageLocationTemp.isLocation })],
        [`${prefix}refStorageLocationTemp.isWaste`]: [requiredWhen({ when: () => !model.refStorageLocationTemp.isLocation })],
        [`${prefix}refStorageLocationTemp.isChemical`]: [requiredWhen({ when: () => !model.refStorageLocationTemp.isLocation })],
        [`${prefix}refStorageLocationTemp.types`]: [required],
        [`${prefix}refStorageLocationTemp.productTypes`]: [required],
        [`${prefix}refStorageLocationTemp.experts`]: [required],
        [`${prefix}refStorageLocationTemp.productionArea`]: [requiredWhen({ when: () => !model.refStorageLocationTemp.isLocation })],
        [`${prefix}refStorageLocationTemp.horsepower`]: [requiredWhen({ when: () => !model.refStorageLocationTemp.isLocation })],
        [`${prefix}refStorageLocationTemp.worker`]: [requiredWhen({ when: () => !model.refStorageLocationTemp.isLocation })]
    };
    model?.refStorageLocationTemp?.experts?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refStorageLocationTemp.experts.${index}.firstName`] = [required];
            rules[`${prefix}refStorageLocationTemp.experts.${index}.lastName`] = [required];
            rules[`${prefix}refStorageLocationTemp.experts.${index}.idCard`] = [required, noHyphenValidator(), isAlphabetOrNumber];
            rules[`${prefix}refStorageLocationTemp.experts.${index}.tel`] = [required];
            rules[`${prefix}refStorageLocationTemp.experts.${index}.email`] = [required, email];
        }
    });
    return rules;
};

export const createRulesTab3 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}refStorageLocationTemp.subs`]: [requiredWhen({ when: () => model.refStorageLocationTemp.isLocation })]
    };
    model?.refStorageLocationTemp?.subs?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refStorageLocationTemp.subs.${index}.refType`] = [required];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.buildingName`] = [required];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.totalWidth`] = [
                requiredWhen({ when: () => model.refStorageLocationTemp.subs[index].refType?.key === "1" })
            ];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.totalLong`] = [
                requiredWhen({ when: () => model.refStorageLocationTemp.subs[index].refType?.key === "1" }),
                {
                    validator: () => {
                        const width = model.refStorageLocationTemp.subs[index].totalWidth;
                        const long = model.refStorageLocationTemp.subs[index].totalLong;
                        if (long?.toString().length !== 0 && width?.toString().length !== 0) {
                            if (parseFloat(long) < parseFloat(width)) {
                                return Promise.reject(i18n.global.t("views.request.storageLocationSub.error.widthGreaterLong"));
                            }
                        }
                        return Promise.resolve();
                    },
                    trigger: "change"
                }
            ];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.totalHigh`] = [
                requiredWhen({ when: () => model.refStorageLocationTemp.subs[index].refType?.key === "1" })
            ];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.totalArea`] = [required];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.totalCapacity`] = [required];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.useWidth`] = [
                requiredWhen({ when: () => model.refStorageLocationTemp.subs[index].refType?.key === "1" }),
                {
                    validator: () => {
                        const useWidth = model.refStorageLocationTemp.subs[index].useWidth;
                        const totalWidth = model.refStorageLocationTemp.subs[index].totalWidth;
                        if (useWidth?.toString().length !== 0 && totalWidth?.toString().length !== 0) {
                            if (parseFloat(useWidth) > parseFloat(totalWidth)) {
                                return Promise.reject(i18n.global.t("views.request.storageLocationSub.error.useWidthGreaterTotalWidth"));
                            }
                        }
                        return Promise.resolve();
                    },
                    trigger: "change"
                }
            ];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.useLong`] = [
                requiredWhen({ when: () => model.refStorageLocationTemp.subs[index].refType?.key === "1" }),
                {
                    validator: () => {
                        const useLong = model.refStorageLocationTemp.subs[index].useLong;
                        const totalLong = model.refStorageLocationTemp.subs[index].totalLong;
                        if (useLong?.toString().length !== 0 && totalLong?.toString().length !== 0) {
                            if (parseFloat(useLong) > parseFloat(totalLong)) {
                                return Promise.reject(i18n.global.t("views.request.storageLocationSub.error.useLongGreaterTotalLong"));
                            }
                        }
                        return Promise.resolve();
                    },
                    trigger: "change"
                },
                {
                    validator: () => {
                        const width = model.refStorageLocationTemp.subs[index].useWidth;
                        const long = model.refStorageLocationTemp.subs[index].useLong;
                        if (long?.toString().length !== 0 && width?.toString().length !== 0) {
                            if (parseFloat(long) < parseFloat(width)) {
                                return Promise.reject(i18n.global.t("views.request.storageLocationSub.error.widthGreaterLong"));
                            }
                        }
                        return Promise.resolve();
                    },
                    trigger: "change"
                }
            ];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.useHigh`] = [
                requiredWhen({ when: () => model.refStorageLocationTemp.subs[index].refType?.key === "1" }),
                {
                    validator: () => {
                        const useHigh = model.refStorageLocationTemp.subs[index].useHigh;
                        const totalHigh = model.refStorageLocationTemp.subs[index].totalHigh;
                        if (useHigh?.toString().length !== 0 && totalHigh?.toString().length !== 0) {
                            if (parseFloat(useHigh) > parseFloat(totalHigh)) {
                                return Promise.reject(i18n.global.t("views.request.storageLocationSub.error.useHighGreaterTotalHigh"));
                            }
                        }
                        return Promise.resolve();
                    },
                    trigger: "change"
                }
            ];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.useArea`] = [required];
            rules[`${prefix}refStorageLocationTemp.subs.${index}.useCapacity`] = [
                required,
                {
                    validator: () => {
                        const totalCapacity = model.refStorageLocationTemp.subs[index].totalCapacity;
                        const useCapacity = model.refStorageLocationTemp.subs[index].useCapacity;
                        if (useCapacity?.toString().length !== 0 && totalCapacity?.toString().length !== 0) {
                            if (parseFloat(useCapacity) > parseFloat(totalCapacity)) {
                                return Promise.reject(i18n.global.t("views.request.storageLocationSub.error.useGreaterTotal"));
                            }
                        }
                        return Promise.resolve();
                    },
                    trigger: "change"
                }
            ];
            //rules[`${prefix}refStorageLocationTemp.subs.${index}.useKg`] = [required];
        }
    });
    return rules;
};

export const createRulesTab4 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refStorageLocationTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refStorageLocationTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refStorageLocationTemp.attachments.${index}.size`] = [
                requiredWhen({
                    when: () =>
                        model.refStorageLocationTemp.attachments[index].attribute?.isRequiredAttachment ||
                        model.refStorageLocationTemp.attachments[index].attribute?.documentNo
                })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, isLocation, forceDefault } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callStorageLocationAttachment({}),
        callManufactureLocationAttachment({}),
        callAddressType({}),
        callRequestDoc({}),
        callRequestStatus({}),
        callRequestType({}),
        callRequestDocGenType({})
    ];
    return Promise.all(promises).then(arr => {
        data.storageLocationAttachments = isLocation ? arr[0] : arr[1];
        data.addressTypes = arr[2];
        data.requestDocs = arr[3];
        data.statuses = arr[4];
        data.requestType = arr[5];
        data.requestDocGenTypes = arr[6];
        const userInfo = store.state.resource.auth.info?.user;

        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            modelRef.refStorageLocationTemp.isLocation = isLocation;
            if (!modelRef.isOfficerCreate) {
                callUserCompanyMe({}).then(userCompanies => {
                    const refUserCompany = userCompanies[0];
                    modelRef.refStorageLocationTemp.refUserCompany = refUserCompany;
                    if (refUserCompany) {
                        callUser({ userCompanyId: refUserCompany.id }).then(
                            users => (modelRef.refStorageLocationTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id))
                        );
                    }
                });
            }
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === (isLocation ? "06" : "07"));
            modelRef.refRequestType = data.requestType.find(d => d.key === "1");
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");
            modelRef.refStorageLocationTemp.addresses[0].refAddressType = data.addressTypes.find(d => d.key === "1");
            data.storageLocationAttachments.forEach(item =>
                modelRef.refStorageLocationTemp.attachments.push({ documentName: item.value, attribute: lodash.cloneDeep(item.attribute) })
            );
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix, isLocation } = props;
    if (stateGroup[isLocation]) {
        return stateGroup[isLocation];
    }
    const state = {
        columns: createColumns({ props }),
        page: createPage({
            options: {
                select: [
                    "*",
                    "refStorageLocation.*",
                    "refStorageLocationTemp.*",
                    "refStorageLocationTemp.refUserCompany.*",
                    "refStorageLocationTemp.refUserCompany.refPersonType.*",
                    "refStorageLocationTemp.refUser.*",
                    "refRequestType.*",
                    "refStatus.*"
                ],
                sort: [
                    /* { name: "refStatus.seq", type: "ASC" } ,*/
                    { name: "updateDate", type: "DESC" }
                ],
                children: [
                    {
                        name: "refStorageLocationTemp.addresses",
                        select: ["refProvince.*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    }
                ]
            }
        }),
        tableOptions: { search: { activeKey: "Search" } },
        conditionAction: createConditionAction()
    };
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, state, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, state, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction.canReStatus({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportStorageLocation({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/company/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
    return (stateGroup[isLocation] = state);
};

export const initForm = ({ props, data, refs, state }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    data.userInfo = store.state.resource.auth.info?.user;
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refStorageLocationTemp.experts.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab2({ props, data, refs }),
        itemsKey: "refStorageLocationTemp.experts"
    });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refStorageLocationTemp.subs.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab3({ props, data, refs }),
        itemsKey: "refStorageLocationTemp.subs"
    });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refStorageLocationTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab4({ props, data, refs }),
        itemsKey: "refStorageLocationTemp.attachments"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs, state })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs, state }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refStorageLocationTemp = modelRef.refStorageLocationTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forReceiveRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        state,
                        formParams: {
                            refStorageLocationTemp: {
                                ...modelRef.refStorageLocationTemp,
                                refUserCompany: refStorageLocationTemp?.refUserCompany?.id ? refStorageLocationTemp?.refUserCompany : null,
                                refUser: refStorageLocationTemp?.refUser?.id ? refStorageLocationTemp?.refUser : null
                            },
                            statusRemark
                        }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmRequest({
                            onOk: record => {
                                const refStorageLocationTemp = modelRef.refStorageLocationTemp;
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    state,
                                    formParams: {
                                        refStorageLocationTemp: {
                                            ...modelRef.refStorageLocationTemp,
                                            refUserCompany: refStorageLocationTemp?.refUserCompany?.id
                                                ? refStorageLocationTemp?.refUserCompany
                                                : null,
                                            refUser: refStorageLocationTemp?.refUser?.id ? refStorageLocationTemp?.refUser : null
                                        },
                                        statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                        refStatus: statusWaitReceive,
                                        isSignCa: record.isSignCa,
                                        passwordCa: record.passwordCa
                                    },
                                    successText: i18n.global.t("views.request.modal.request.sendSuccess")
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmReceive({
                            props: {
                                roleName: modelRef.refStorageLocationTemp.isLocation ? "STORAGE_LOCATION_APPROVE" : "MANUFACTURE_LOCATION_APPROVE",
                                statusKeys: ["draft"].includes(initialModel.refStatus?.key) ? ["waitApprove"] : []
                            },
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    state,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forReceiveRemark[0].remark : null,
                                        refStatus: record.refStatus,
                                        refToApproveBy: record.refToApproveBy
                                    },
                                    successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    state,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forReceiveRemark[0].remark : null,
                                        refStatus: record.refStatus
                                    },
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const stateGroup = [];
